import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Check, ArrowRight, ArrowLeft, Menu, X } from "lucide-react";

const CLASSES = [
  {
    title: "Best Hook Pack — PDF",
    tagline: "The scroll-stoppers everyone's borrowing",
    body: "A downloadable pack of the highest-performing hooks used to open reels, carousels &amp; captions. Steal, tweak, post. No fluff — just the hooks that make people stop scrolling.",
    price: 9,
    thumb:
      "https://cdn.courses.apisystem.tech/memberships/00rUS8kTZMl2DZiQRsgl/offer/a565a799-b2a9-4c02-aa3e-1a4b95202f7a-ln60",
    href: "https://aiforbossbabes.app.clientclub.net/courses/offers/a565a799-b2a9-4c02-aa3e-1a4b95202f7a",
    tag: "Copy · PDF",
    bullets: [
      "Plug-and-play hook formulas",
      "Written for coaches, creators & realtors",
      "Instant download — use it in the next 5 minutes",
    ],
  },
  {
    title: "Canva Carousel with AI",
    tagline: "Design scroll-stopping carousels in minutes",
    body: "Learn how to create high-quality Canva carousels using AI — the exact workflow used to turn 1 idea into 7 pieces of content, without spending hours in design mode.",
    price: 27,
    thumb:
      "https://storage.googleapis.com/revex-membership-production/memberships/00rUS8kTZMl2DZiQRsgl/courses/Neutral-Modern-Lifestyle-Vlog-YouTube-Thumbnail-lzvr.png",
    href: "https://aiforbossbabes.app.clientclub.net/courses/offers/7313266f-5482-49df-b73d-f109a8fb0022",
    tag: "Content · AI",
    bullets: [
      "The AI + Canva workflow (start to finish)",
      "Templates you can use the same day",
      "Turn 1 idea into 7 pieces of content",
    ],
  },
  {
    title: "Instagram Trial Reels — Step by Step",
    tagline: "The risk-free way onto Instagram's second feed",
    body: "Instagram runs two feeds — one for your followers, one for everyone who's never heard of you. Trial Reels is your direct line into the second one. 6 lessons, watch and use the same day.",
    price: 47,
    thumb:
      "https://cdn.courses.apisystem.tech/memberships/00rUS8kTZMl2DZiQRsgl/courses/Copy-of-CLASS-THUMBNAILS-hh3i.png",
    href: "https://aiforbossbabes.app.clientclub.net/courses/offers/506339bb-b1e0-4e33-aa16-b55fe9c82483",
    tag: "Instagram · Growth",
    bullets: [
      "Best posting times + hooks that get tested",
      "Read Trial Reels results like data, not guesswork",
      "The 90-minute weekly content system",
    ],
  },
];

export default function ClassesPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const close = () => setMenuOpen(false);

  // Per-page SEO — updates browser tab + meta description when visiting /classes
  useEffect(() => {
    const prevTitle = document.title;
    document.title =
      "Classes — AI for Boss Babes | Canva Carousels & Instagram Trial Reels";
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc) {
      desc.setAttribute(
        "content",
        "Bite-sized social media classes for female creators. Canva Carousel with AI ($27) & Instagram Trial Reels Step by Step ($47). Watch it, apply it, post it — the same day."
      );
    }
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc !== null) desc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="relative min-h-screen font-body bg-[var(--cream)]">
      {/* Header */}
      <header
        data-testid="classes-header"
        className="sticky top-0 z-50 bg-[#fff4ec]/85 backdrop-blur-md border-b-2 border-[var(--ink)]"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="classes-brand-logo"
          >
            <div className="w-9 h-9 bg-[var(--burgundy)] border-2 border-[var(--ink)] rounded-full flex items-center justify-center -rotate-6">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-xl">
              <span className="font-script text-3xl text-[var(--burgundy)] align-middle mr-2">
                ai
              </span>
              for boss babes
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-[var(--ink)]/70">
            <Link
              to="/"
              className="hover:text-[var(--coral)]"
              data-testid="classes-nav-home"
            >
              Home
            </Link>
            <a
              href="/#offer"
              className="hover:text-[var(--coral)]"
              data-testid="classes-nav-offer"
            >
              The Offer
            </a>
            <a
              href="/#faq"
              className="hover:text-[var(--coral)]"
              data-testid="classes-nav-faq"
            >
              FAQ
            </a>
          </nav>

          <Link
            to="/"
            className="hidden md:inline-flex items-center gap-1.5 bg-[var(--burgundy)] hover:bg-[var(--burgundy-hover)] text-white text-sm font-bold px-4 py-2 rounded-full border-2 border-[var(--ink)] shadow-[3px_3px_0_var(--ink)] transition-all"
            data-testid="classes-header-cta"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full bg-white border-2 border-[var(--ink)] shadow-[3px_3px_0_var(--ink)] hover:bg-[var(--lemon)] transition-colors"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            data-testid="classes-mobile-menu-toggle"
          >
            {menuOpen ? (
              <X className="w-5 h-5 text-[var(--ink)]" strokeWidth={2.5} />
            ) : (
              <Menu className="w-5 h-5 text-[var(--ink)]" strokeWidth={2.5} />
            )}
          </button>
        </div>

        {/* Mobile drawer */}
        {menuOpen && (
          <div
            className="md:hidden border-t-2 border-[var(--ink)] bg-[#fff4ec] shadow-lg"
            data-testid="classes-mobile-menu-drawer"
          >
            <nav className="flex flex-col divide-y divide-[var(--ink)]/10">
              <Link to="/" onClick={close} className="px-6 py-4 font-semibold text-[var(--ink)] hover:bg-[var(--lemon)]" data-testid="classes-mobile-nav-home">Home</Link>
              <a href="/#offer" onClick={close} className="px-6 py-4 font-semibold text-[var(--ink)] hover:bg-[var(--lemon)]" data-testid="classes-mobile-nav-offer">The Offer</a>
              <a href="/#faq" onClick={close} className="px-6 py-4 font-semibold text-[var(--ink)] hover:bg-[var(--lemon)]" data-testid="classes-mobile-nav-faq">FAQ</a>
              <Link
                to="/"
                onClick={close}
                className="px-6 py-4 font-bold text-white bg-[var(--burgundy)] hover:bg-[var(--burgundy-hover)] flex items-center justify-between"
                data-testid="classes-mobile-nav-cta"
              >
                <span className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back to home</span>
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section
        data-testid="classes-hero"
        className="relative py-20 md:py-28 section-cream overflow-hidden"
      >
        <div className="blob bg-[var(--lemon)] w-[500px] h-[500px] -top-20 -right-20 opacity-60" />
        <div className="blob bg-[var(--coral)] w-[350px] h-[350px] -bottom-16 -left-10 opacity-30" />

        <div className="relative max-w-4xl mx-auto px-5 md:px-8 text-center">
          <span className="tag-pill mb-6 bg-[var(--burgundy)] text-white">
            the classes
          </span>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-[var(--ink)] leading-[0.95] mb-6">
            Learn the systems.
            <br />
            <span className="italic text-[var(--burgundy)]">Grow on repeat.</span>
          </h1>
          <p className="text-[var(--ink)]/70 text-lg md:text-xl max-w-2xl mx-auto">
            Bite-sized, no-fluff trainings for busy babes. Watch it, apply it,
            post it — the same day.
          </p>
        </div>
      </section>

      {/* Class grid */}
      <section className="relative py-16 md:py-24 section-cream">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-8">
            {CLASSES.map((c) => (
              <a
                key={c.href}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`class-card-${c.title
                  .split(" ")[0]
                  .toLowerCase()}`}
                className="group bg-white border-2 border-[var(--ink)] rounded-[28px] shadow-[8px_8px_0_var(--ink)] overflow-hidden hover:shadow-[12px_12px_0_var(--ink)] hover:-translate-y-1 transition-all duration-200 flex flex-col"
              >
                <div className="relative w-full aspect-video overflow-hidden bg-[var(--lemon)] border-b-2 border-[var(--ink)]">
                  <img
                    src={c.thumb}
                    alt={c.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-3 left-3 bg-white border-2 border-[var(--ink)] text-[var(--ink)] text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-1.5 rounded-full">
                    {c.tag}
                  </span>
                </div>

                <div className="p-6 md:p-7 flex-1 flex flex-col">
                  <h3 className="font-display text-2xl md:text-3xl text-[var(--ink)] leading-tight mb-2">
                    {c.title}
                  </h3>
                  <p className="text-[var(--burgundy)] font-semibold italic text-sm md:text-base mb-4">
                    {c.tagline}
                  </p>
                  <p className="text-[var(--ink)]/70 text-sm md:text-base leading-relaxed mb-5">
                    {c.body}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {c.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2 text-[var(--ink)] text-sm font-medium"
                      >
                        <Check
                          className="w-4 h-4 text-[var(--burgundy)] mt-0.5 flex-shrink-0"
                          strokeWidth={3}
                        />
                        {b}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto flex items-center justify-between pt-4 border-t-2 border-dashed border-[var(--ink)]/15">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[var(--ink)]/50 font-bold mb-0.5">
                        Enroll
                      </div>
                      <div className="font-display text-3xl text-[var(--burgundy)]">
                        ${c.price}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-2 bg-[var(--ink)] text-white font-bold px-5 py-3 rounded-full border-2 border-[var(--ink)] group-hover:bg-[var(--burgundy)] transition-colors">
                      Enroll now
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Footer nudge back to $27 offer */}
      <section
        className="relative py-16 md:py-20 section-ink border-t-2 border-[var(--ink)] overflow-hidden"
        data-testid="classes-footer-cta"
      >
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          <span className="tag-pill mb-5 bg-[var(--lemon)] text-[var(--ink)]">
            not sure where to start?
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-white mb-5 leading-tight">
            Get your first AI photos +{" "}
            <span className="text-[var(--lemon)] italic">reel in minutes</span>.
          </h2>
          <p className="text-white/70 text-base md:text-lg mb-8 max-w-xl mx-auto">
            The AI Twin studio is where most babes begin. Unlock 4 scroll-stopping photos + 1 custom reel for just $27.
          </p>
          <Link
            to="/#offer"
            data-testid="classes-back-to-offer"
            className="inline-flex items-center gap-2 bg-[var(--lemon)] hover:bg-white text-[var(--ink)] font-bold px-7 py-4 rounded-full border-2 border-[var(--ink)] shadow-[4px_4px_0_var(--ink)] hover:shadow-[6px_6px_0_var(--ink)] hover:-translate-y-0.5 transition-all"
          >
            <Sparkles className="w-4 h-4" />
            Get the AI Twin — $27
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="border-t-2 border-[var(--ink)] py-8 section-cream"
        data-testid="classes-footer"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row items-center justify-between gap-3 text-[var(--ink)]/70 text-sm">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[var(--burgundy)]" />
            <span className="font-display text-lg text-[var(--ink)]">
              <span className="font-script text-2xl text-[var(--burgundy)] mr-1">
                ai
              </span>
              for boss babes
            </span>
            <span className="ml-2">© {new Date().getFullYear()}</span>
          </div>
          <Link
            to="/"
            className="hover:text-[var(--coral)] font-semibold inline-flex items-center gap-1.5"
            data-testid="classes-footer-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </footer>
    </div>
  );
}
